// Pure Projects Travel helpers: URL state, zone choices, purpose styling,
// month layout, display formatting and the Add/Edit payload builder.
// All civil-date / wall-time conversion routes through src/lib/travelTime.js.
import { T } from "../../../app/tokens.js";
import {
  PURPOSES, isIanaZone, civilDate, localInput, addDays, monthGrid, tripDays, layoutWeek, resolveWallTime, wallTimeCandidates,
} from "../../../lib/travelTime.js";

export const TRAVEL_PREF_KEY = "projects_travel_timezone";
export const TRAVEL_VIEWS = Object.freeze(["month", "list"]);
export const FILTER_PARAMS = Object.freeze({
  travelProject: "Project", travelPerson: "Person", travelMarket: "Market", travelPurpose: "Purpose",
});

// ─── Purpose styling (tokenized; muted company events) ──────────────────────
const TONE = {
  site: { bg: T.travelSiteBg, fg: T.travelSiteFg, border: T.travelSiteBg },
  box: { bg: T.travelBoxBg, fg: T.travelBoxFg, border: T.travelBoxBg },
  support: { bg: T.travelSupportBg, fg: T.travelSupportFg, border: T.travelSupportBg },
  event: { bg: T.travelEventBg, fg: T.travelEventFg, border: T.travelEventBorder },
  other: { bg: T.travelOtherBg, fg: T.travelOtherFg, border: T.border },
};
const PURPOSE_TONE = {
  site_milestone: "site", box_walk: "box", design_review: "box",
  office_support: "support", fdob: "support", company_event: "event", other: "other",
};
export function purposeStyle(purpose) { return TONE[PURPOSE_TONE[purpose] || "other"]; }
export function purposeLabel(purpose) { return PURPOSES[purpose] || "Other"; }
export const PURPOSE_KEY = [
  ["site", "Site / construction milestone"],
  ["box", "Box walk · design review"],
  ["support", "Office support · FDOB"],
  ["event", "Company event"],
  ["other", "Other"],
].map(([tone, label]) => ({ tone, label, ...TONE[tone] }));

// ─── URL state ──────────────────────────────────────────────────────────────
export function browserZone() {
  try { const z = Intl.DateTimeFormat().resolvedOptions().timeZone; return isIanaZone(z) ? z : null; }
  catch { return null; }
}
export function validMonth(value) {
  if (!/^\d{4}-\d{2}$/.test(value || "")) return null;
  try { monthGrid(value); return value; } catch { return null; }
}
/** "local" | "UTC" | IANA | null (absent or invalid). */
export function validTzChoice(value) {
  if (value === "local" || value === "UTC") return value;
  return isIanaZone(value) ? value : null;
}
export function resolveZone(choice) {
  // Unknown browser zone is unavailable (null), never silently UTC.
  if (choice === "local" || !choice) return browserZone();
  return choice;
}
export function currentMonth(zone, now = Date.now()) { return civilDate(now, zone).slice(0, 7); }
export function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${String((idx % 12) + 1).padStart(2, "0")}`;
}
export function readTravelUrl(search) {
  const q = new URLSearchParams(search);
  const filters = {};
  for (const key of Object.keys(FILTER_PARAMS)) { const v = q.get(key); if (v) filters[key] = v; }
  return {
    view: q.get("view") === "list" ? "list" : "month",
    month: validMonth(q.get("month")),
    tz: validTzChoice(q.get("tz")),
    invalidTimezone: q.has("tz") && !validTzChoice(q.get("tz")),
    filters,
  };
}
/** Patch Travel params in a search string; null removes. Unrelated params preserved. */
export function patchTravelSearch(search, patch) {
  const q = new URLSearchParams(search);
  for (const [k, v] of Object.entries(patch)) { if (v == null || v === "") q.delete(k); else q.set(k, v); }
  const s = q.toString();
  return s ? `?${s}` : "";
}

// ─── Zone choices (Local resolved, UTC, sourced office/market zones) ────────
export function zoneCity(zone) { return zone === "UTC" ? "UTC" : zone.split("/").pop().replace(/_/g, " "); }
export function zoneAbbr(zone, at = Date.now()) {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(new Date(at)).find((p) => p.type === "timeZoneName")?.value || zone;
  } catch { return zone; }
}
export function zoneChoices(options, extra = null, at = Date.now()) {
  const local = browserZone();
  const zones = new Set();
  for (const row of [...(options?.markets || []), ...(options?.locations || [])]) if (isIanaZone(row?.timeZone) && row.timeZone !== "UTC") zones.add(row.timeZone);
  if (extra && extra !== "local" && extra !== "UTC" && isIanaZone(extra)) zones.add(extra);
  return [
    { value: "local", zone: local, label: local ? `Local · ${zoneCity(local)} (${zoneOffset(local, at)})` : "Local · browser zone unavailable" },
    { value: "UTC", zone: "UTC", label: "UTC (GMT+0)" },
    ...[...zones].sort((a, b) => zoneCity(a).localeCompare(zoneCity(b))).map((z) => ({ value: z, zone: z, label: `${zoneCity(z)} (${zoneOffset(z, at)})` })),
  ];
}

// ─── Formatting ─────────────────────────────────────────────────────────────
export function fmtDateTime(instant, zone, opts = {}) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short", ...opts }).format(new Date(instant));
}
export function fmtCivil(date, opts = { month: "short", day: "numeric" }) {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...opts }).format(new Date(`${date}T00:00:00Z`));
}
export function fmtNow(now, zone) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(new Date(now));
}
export function fmtZoneClock(now, zone) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(new Date(now));
}
export function zoneOffset(zone, at) {
  const offset = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
    .formatToParts(new Date(at)).find(p => p.type === "timeZoneName")?.value;
  return offset === "GMT" ? "GMT+0" : offset;
}
export function fmtUtcClock(now) {
  return new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(now));
}
export function fmtUtcRange(trip) {
  const f = (i) => new Date(i).toISOString().slice(0, 16).replace("T", " ");
  return `${f(trip.startsAt)} → ${f(trip.endsAt)} UTC`;
}
/** Display dates: all-day trips show destination civil dates (inclusive last day). */
export function tripDateRange(trip, zone) {
  const { startDate, endDate } = tripDays(trip, zone);
  const last = addDays(endDate, -1);
  const label = startDate === last ? fmtCivil(startDate) : `${fmtCivil(startDate)} – ${fmtCivil(last)}`;
  return trip.allDay ? `${label} · all day` : label;
}
export function travelerSummary(trip, showNames = true) {
  const n = trip.travelerCount ?? trip.travelers?.length ?? 0;
  if (!n) return "No travelers";
  if (!showNames || !Array.isArray(trip.travelers)) return `${n} traveler${n === 1 ? "" : "s"}`;
  return trip.travelers.map((t) => t.displayName).join(", ");
}
export function holidayLabel(h) {
  if (h?.label) return h.label;
  return h?.scope === "ripple_wide" ? "Ripple-wide non-working day" : `${h?.country || "Country"} non-working day`;
}

// ─── Month layout ───────────────────────────────────────────────────────────
export function holidayDays(h) { return { startDate: h.date, endDate: addDays(h.endDate || h.date, 1) }; }
/** Weeks of the visible grid with holiday lanes first, then stacked trip lanes. */
export function buildMonthWeeks(month, trips, holidays, zone) {
  const { days } = monthGrid(month);
  const tripItems = (trips || []).flatMap((trip) => {
    try { return [{ id: trip.id, kind: "trip", trip, ...tripDays(trip, zone) }]; } catch { return []; }
  });
  const holItems = (holidays || []).map((h) => ({ id: `h:${h.id}`, kind: "holiday", holiday: h, ...holidayDays(h) }));
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    const weekStart = days[i];
    const hol = layoutWeek(holItems, weekStart);
    const offset = hol.reduce((m, x) => Math.max(m, x.lane + 1), 0);
    const tr = layoutWeek(tripItems, weekStart).map((x) => ({ ...x, lane: x.lane + offset }));
    const items = [...hol, ...tr];
    weeks.push({ weekStart, days: days.slice(i, i + 7), items, lanes: items.reduce((m, x) => Math.max(m, x.lane + 1), 0) });
  }
  return weeks;
}
export function sortTrips(trips) {
  return [...(trips || [])].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || String(a.title).localeCompare(String(b.title)));
}

// ─── Editor draft ⇄ payload ─────────────────────────────────────────────────
export const EMPTY_DRAFT = Object.freeze({
  title: "", purposeType: "site_milestone", projectId: "", milestoneId: "",
  destMode: "office", destinationLocationId: "", destinationMarketId: "", destinationLabel: "",
  destinationCountry: "", destinationTz: "", allDay: false,
  startsLocal: "", endsLocal: "", startDate: "", lastDate: "",
  startOccurrence: null, endOccurrence: null, notes: "", travelers: [],
});

export function draftFromTrip(trip) {
  if (!trip) return { ...EMPTY_DRAFT, travelers: [] };
  const tz = trip.destinationTz;
  const safe = (fn) => { try { return fn(); } catch { return ""; } };
  const startsLocal = safe(() => localInput(trip.startsAt, tz));
  const endsLocal = safe(() => localInput(trip.endsAt, tz));
  return {
    title: trip.title || "", purposeType: trip.purposeType || "other",
    projectId: trip.projectId || "", milestoneId: trip.milestoneId || "",
    destMode: trip.destinationLocationId ? "office" : trip.destinationMarketId ? "market" : "free",
    destinationLocationId: trip.destinationLocationId || "", destinationMarketId: trip.destinationMarketId || "",
    destinationLabel: trip.destinationLabel || "", destinationCountry: trip.destinationCountry || "", destinationTz: tz || "",
    allDay: Boolean(trip.allDay), startsLocal, endsLocal,
    startDate: startsLocal.slice(0, 10), lastDate: endsLocal ? safe(() => addDays(endsLocal.slice(0, 10), -1)) : "",
    startOccurrence: null, endOccurrence: null, notes: trip.notes || "",
    travelers: (trip.travelers || []).map((t) => ({
      personId: t.personId, displayName: t.displayName, org: t.org, active: t.active !== false, existing: true,
      override: Boolean(t.startsAtOverride || t.endsAtOverride),
      startsLocal: t.startsAtOverride ? safe(() => localInput(t.startsAtOverride, tz)) : "",
      endsLocal: t.endsAtOverride ? safe(() => localInput(t.endsAtOverride, tz)) : "",
      startOccurrence: null, endOccurrence: null,
    })),
  };
}

/** "ok" | "gap" | "fold" | "invalid" for a destination wall time. */
export function wallTimeStatus(local, zone) {
  if (!local || !isIanaZone(zone)) return "invalid";
  try {
    const n = wallTimeCandidates(local, zone).length;
    return n === 0 ? "gap" : n > 1 ? "fold" : "ok";
  } catch { return "invalid"; }
}

/** Effective parent wall times (all-day → destination midnights, end exclusive). */
export function draftBounds(draft) {
  if (draft.allDay) {
    let end = "";
    try { end = draft.lastDate ? `${addDays(draft.lastDate, 1)}T00:00` : ""; } catch { end = ""; }
    return { startsLocal: draft.startDate ? `${draft.startDate}T00:00` : "", endsLocal: end };
  }
  return { startsLocal: draft.startsLocal, endsLocal: draft.endsLocal };
}

export function validateDraft(draft) {
  const errors = {};
  if (!draft.title.trim()) errors.title = "Title is required.";
  if (draft.title.length > 200) errors.title = "Keep the title under 200 characters.";
  if (draft.milestoneId && !draft.projectId) errors.milestoneId = "Pick a project before a milestone.";
  if (draft.destMode === "office" && !draft.destinationLocationId) errors.destination = "Choose an office.";
  if (draft.destMode === "market" && !draft.destinationMarketId) errors.destination = "Choose a market.";
  if (!draft.destinationLabel.trim()) errors.destinationLabel = "Destination label is required.";
  if (!draft.destinationCountry.trim()) errors.destinationCountry = "Destination country is required.";
  if (!isIanaZone(draft.destinationTz)) errors.destinationTz = "A valid IANA time zone is required (e.g. Europe/London). Unknown zones are unavailable, never assumed UTC.";
  const { startsLocal, endsLocal } = draftBounds(draft);
  let start = null; let end = null;
  if (!errors.destinationTz) {
    const resolve = (local, occ, key) => {
      if (!local) { errors[key] = "Required."; return null; }
      try { return Date.parse(resolveWallTime(local, draft.destinationTz, occ)); } catch (e) { errors[key] = e.message; return null; }
    };
    start = resolve(startsLocal, draft.allDay ? null : draft.startOccurrence, "start");
    end = resolve(endsLocal, draft.allDay ? null : draft.endOccurrence, "end");
    if (start != null && end != null && end <= start) errors.end = draft.allDay ? "The last day must be on or after the first day." : "End must be after start.";
    draft.travelers.forEach((t, i) => {
      if (!t.override) return;
      const s = resolve(t.startsLocal, t.startOccurrence, `traveler${i}Start`);
      const e = resolve(t.endsLocal, t.endOccurrence, `traveler${i}End`);
      if (s != null && e != null) {
        if (e <= s) errors[`traveler${i}End`] = "Override end must be after its start.";
        else if (start != null && end != null && (s < start || e > end)) errors[`traveler${i}End`] = "Override must sit within the trip dates.";
      }
    });
  }
  const seen = new Set();
  for (const t of draft.travelers) { if (seen.has(t.personId)) errors.travelers = "A person can only be listed once."; seen.add(t.personId); }
  return errors;
}

export function buildTripPayload(draft) {
  const { startsLocal, endsLocal } = draftBounds(draft);
  return {
    title: draft.title.trim(),
    purposeType: draft.purposeType,
    projectId: draft.projectId || null,
    milestoneId: draft.projectId && draft.milestoneId ? draft.milestoneId : null,
    destinationLocationId: draft.destMode === "office" ? draft.destinationLocationId || null : null,
    destinationMarketId: draft.destMode === "free" ? null : draft.destinationMarketId || null,
    destinationLabel: draft.destinationLabel.trim(),
    destinationCountry: draft.destinationCountry.trim(),
    destinationTz: draft.destinationTz.trim(),
    startsLocal, endsLocal,
    startOccurrence: draft.startOccurrence || null,
    endOccurrence: draft.endOccurrence || null,
    allDay: Boolean(draft.allDay),
    notes: draft.notes,
    travelers: draft.travelers.map((t) => ({
      personId: t.personId,
      startsLocalOverride: t.override ? t.startsLocal || null : null,
      endsLocalOverride: t.override ? t.endsLocal || null : null,
      startOccurrence: t.override ? t.startOccurrence || null : null,
      endOccurrence: t.override ? t.endOccurrence || null : null,
    })),
  };
}

export function countryChoices(options) {
  const set = new Set(Array.isArray(options?.countries) ? options.countries.filter(Boolean) : []);
  for (const row of [...(options?.locations || []), ...(options?.markets || [])]) if (row?.country) set.add(row.country);
  return [...set].sort((a, b) => a.localeCompare(b));
}
